import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useFormik } from 'formik';
import { Button } from 'primereact/button';
import { InputText } from "primereact/inputtext";
import { Password } from 'primereact/password';
import { classNames } from 'primereact/utils';
import AuthLayout, { AuthField } from "../../modules/AuthLayout";
import { send, notify } from "../../../services/NotificationService";
import { login } from "../../../data/auth.service";

export default function Login() {
    const [loading, setLoading] = useState(false);

    // URL canonique de la page de connexion (référencement).
    useEffect(() => {
        const canonicalHref = `${window.location.origin}/login`;
        let link = document.querySelector("link[rel='canonical']");
        const previousHref = link?.getAttribute("href") ?? null;
        const created = !link;

        if (!link) {
            link = document.createElement("link");
            link.setAttribute("rel", "canonical");
            document.head.appendChild(link);
        }

        link.setAttribute("href", canonicalHref);

        return () => {
            if (!link) {
                return;
            }

            if (created) {
                document.head.removeChild(link);
                return;
            }

            if (previousHref) {
                link.setAttribute("href", previousHref);
            } else {
                link.removeAttribute("href");
            }
        };
    }, []);

    const formik = useFormik({
        initialValues: {
            email: '',
            password: ''
        },
        validate: (data) => {
            const errors = {};
            if (!data.email) {
                errors.email = "Veuillez saisir votre adresse e-mail.";
            }
            if (!data.password) {
                errors.password = "Veuillez saisir votre mot de passe.";
            }
            return errors;
        },
        onSubmit: async (data) => {
            setLoading(true);

            let ret;
            try {
                ret = await login(data);
            } catch (error) {
                send("error", "Connexion impossible pour le moment. Veuillez réessayer dans quelques instants.");
                notify();
                setLoading(false);
                return;
            }

            if (ret.status === 401) {
                send("warning", "Identifiant ou mot de passe invalide.");
                notify();
                setLoading(false);
                return;
            }

            if (ret.redirectToNewSite) {
                localStorage.removeItem("user");
                localStorage.removeItem("holidaysMode");
                window.location.replace("https://lokapp.fr/Account/Login?redirected=1");
                return;
            }

            if (ret.user && ret.user.roles) {
                window.location.replace("/");
                return;
            }

            send("warning", ret.message || "Connexion impossible. Vérifiez vos identifiants.");
            notify();
            setLoading(false);
        }
    });

    const isFormFieldInvalid = (name) => !!(formik.touched[name] && formik.errors[name]);

    return (
        <AuthLayout
            title="Connexion"
            subtitle="Accédez à votre espace loueur."
            footer={<>Pas encore d'espace loueur ? <a href="https://lokapp.fr/">Découvrez Lokapp</a></>}
        >
            <form onSubmit={formik.handleSubmit} noValidate>
                <AuthField id="email" label="Adresse e-mail" error={isFormFieldInvalid('email') ? formik.errors.email : null}>
                    <InputText
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        inputMode="email"
                        value={formik.values.email}
                        data-cy="emailInput"
                        onChange={(e) => formik.setFieldValue('email', e.target.value)}
                        className={classNames({ 'p-invalid': isFormFieldInvalid('email') })}
                    />
                </AuthField>

                <AuthField
                    id="password"
                    label="Mot de passe"
                    action={<Link to="/reset">Mot de passe oublié ?</Link>}
                    error={isFormFieldInvalid('password') ? formik.errors.password : null}
                >
                    <Password
                        inputId="password"
                        name="password"
                        autoComplete="current-password"
                        value={formik.values.password}
                        data-cy="passwordInput"
                        feedback={false}
                        toggleMask
                        onChange={(e) => formik.setFieldValue('password', e.target.value)}
                        className={classNames({ 'p-invalid': isFormFieldInvalid('password') })}
                    />
                </AuthField>

                <div className="lk-auth__actions">
                    <Button type="submit" label="Se connecter" icon="pi pi-sign-in" loading={loading} data-cy="connexionBtn" />
                </div>
            </form>
        </AuthLayout>
    );
}
